/**
 * Combine Gate Component
 * Sentence combining exercise using connectors
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CombineQuestion, GateEvaluationResult } from '../../types/writing';
import { ClickableText } from '../ClickableWord';
import './GateComponents.css';

interface CombineGateProps {
  question: CombineQuestion;
  onSubmit: (answer: string) => void;
  submitting: boolean;
  result: GateEvaluationResult | null;
  onSaveToNotes?: () => void;
  onSaveItemToNotes?: (item: string) => void;
}

const RELATIONSHIP_KEYS: Record<string, string> = {
  causal: 'combineGate.causal',
  contrast: 'combineGate.contrast',
  addition: 'combineGate.addition',
  temporal: 'combineGate.temporal',
  conditional: 'combineGate.conditional',
};

/**
 * Safely extract sentences array from potentially malformed question data
 */
const extractSentences = (question: CombineQuestion): string[] => {
  // If sentences is already an array of strings
  if (Array.isArray(question.sentences)) {
    return question.sentences.map(s => {
      if (typeof s === 'string') return s;
      // Handle nested object like {sentences: [...], exampleAnswer: "..."}
      if (s && typeof s === 'object') {
        if ('sentences' in s && Array.isArray((s as any).sentences)) {
          return (s as any).sentences.join(' ');
        }
        return JSON.stringify(s);
      }
      return String(s);
    });
  }
  // If sentences is an object with nested sentences array
  if (question.sentences && typeof question.sentences === 'object' && 'sentences' in (question.sentences as any)) {
    const nested = (question.sentences as any).sentences;
    if (Array.isArray(nested)) {
      return nested.map((s: any) => typeof s === 'string' ? s : String(s));
    }
  }
  return [];
};

/**
 * Safely extract connectors array
 */
const extractConnectors = (question: CombineQuestion): string[] => {
  if (!question.suggestedConnectors) return [];
  if (Array.isArray(question.suggestedConnectors)) {
    return question.suggestedConnectors.map(c => typeof c === 'string' ? c : String(c));
  }
  return [];
};

/**
 * Safely extract translation string
 */
const extractTranslation = (question: CombineQuestion): string | null => {
  if (!question.translation) return null;
  if (typeof question.translation === 'string') return question.translation;
  if (typeof question.translation === 'object') {
    // If it's an object, try to get a string value from it
    const t = question.translation as any;
    if (t.zh) return t.zh;
    if (t.en) return t.en;
    return JSON.stringify(t);
  }
  return String(question.translation);
};

const CombineGate: React.FC<CombineGateProps> = ({
  question,
  onSubmit,
  submitting,
  result,
  onSaveToNotes,
  onSaveItemToNotes,
}) => {
  const { t } = useTranslation(['writing', 'common']);
  const [answer, setAnswer] = useState('');

  // Reset answer when question changes
  useEffect(() => {
    setAnswer('');
  }, [question.id]);

  const handleSubmit = () => {
    if (answer.trim() && !submitting) {
      onSubmit(answer.trim());
    }
  };

  // Safely extract data from potentially malformed question
  const sentences = extractSentences(question);
  const connectors = extractConnectors(question);
  const translation = extractTranslation(question);

  const relationshipKey = typeof question.relationship === 'string' ? question.relationship : 'causal';
  const relationshipI18nKey = RELATIONSHIP_KEYS[relationshipKey];
  const relationshipLabel = relationshipI18nKey ? t(relationshipI18nKey) : relationshipKey;

  return (
    <div className="gate-question combine-gate">
      {/* Question */}
      <div className="gate-question__card">
        <div className="gate-question__header">
          <span className="gate-question__type">{t('sentenceCombining')}</span>
        </div>

        <div className="gate-question__content">
          <p className="combine-instruction">
            {t('combineInstructions')}
          </p>

          {/* Relationship tag */}
          <div className="combine-relationship">
            <span className="combine-relationship__label">{t('relationship')}:</span>
            <span className="combine-relationship__value">
              {relationshipLabel}
            </span>
          </div>

          {/* Original sentences */}
          <div className="combine-sentences">
            {sentences.map((sentence, index) => (
              <div key={index} className="combine-sentence">
                <span className="combine-sentence__number">{index + 1}</span>
                <span className="combine-sentence__text">
                  <ClickableText text={sentence} />
                </span>
              </div>
            ))}
          </div>

          {/* Suggested connectors */}
          <div className="combine-connectors">
            <span className="combine-connectors__label">{t('suggestedConnectors')}:</span>
            <div className="combine-connectors__list">
              {connectors.map((connector, index) => (
                <span key={index} className="combine-connector">
                  <ClickableText text={connector} />
                </span>
              ))}
            </div>
          </div>

          {translation && (
            <p className="gate-question__translation">{translation}</p>
          )}
        </div>

        {/* Answer input */}
        {!result && (
          <div className="combine-input-container">
            <textarea
              className="combine-input"
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              placeholder={t('writeYourCombinedSentence')}
              disabled={submitting}
              rows={3}
              autoFocus
            />
            <div className="combine-input-hint">
              {t('combineHint')}
            </div>
          </div>
        )}

        {/* Submitted answer display */}
        {result && (
          <div className="combine-submitted">
            <span className="combine-submitted__label">{t('yourAnswer')}:</span>
            <p className="combine-submitted__text">{answer}</p>
          </div>
        )}
      </div>

      {/* Result Feedback */}
      {result && (
        <div className={`gate-feedback ${result.passed ? 'pass' : 'fail'}`}>
          <div className="gate-feedback__header">
            <span className="gate-feedback__status">
              {result.passed ? '✓ ' + t('wellDone') : '✗ ' + t('needsImprovement')}
            </span>
            <span className="gate-feedback__score">{result.score}%</span>
          </div>
          <p className="gate-feedback__text"><ClickableText text={result.feedback} /></p>

          {result.grammarIssues && result.grammarIssues.length > 0 && (
            <div className="gate-feedback__issues">
              <span className="gate-feedback__issues-label">{t('grammarIssues')}:</span>
              <ul>
                {result.grammarIssues.map((issue, index) => (
                  <li key={index} className="gate-feedback__item">
                    <span className="gate-feedback__item-text"><ClickableText text={issue} /></span>
                    {onSaveItemToNotes && (
                      <button
                        className="gate-feedback__item-save-btn"
                        onClick={() => onSaveItemToNotes(issue)}
                        title={t('saveToNotes')}
                      >+</button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {result.improvedVersion && (
            <div className="gate-feedback__improved">
              <span className="gate-feedback__improved-label">{t('betterVersion')}:</span>
              <span className="gate-feedback__improved-text">
                <ClickableText text={result.improvedVersion} />
              </span>
            </div>
          )}

          {onSaveToNotes && (
            <button className="gate-feedback__save-note-btn" onClick={onSaveToNotes}>
              {t('saveToNotes')}
            </button>
          )}
        </div>
      )}

      {/* Submit Button */}
      {!result && (
        <button
          className="gate-submit-btn"
          onClick={handleSubmit}
          disabled={!answer.trim() || submitting}
        >
          {submitting ? t('submitting') : t('submit')}
        </button>
      )}
    </div>
  );
};

export default CombineGate;
